import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Upload, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Textarea } from '@/app/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  useControlledDocument,
  useCreateAndApproveControlledDocument,
  useCreateControlledDocument,
  useControlledDocumentOwnerOptions,
  useUpdateControlledDocument,
} from '@/hooks/use-documents';
import { useAuth } from '@/contexts/auth-context';
import { toast } from '@/app/components/ui/simple-toaster';
import { handleApiError } from '@/lib/api-client';
import type { DocumentCategory } from '@/lib/types';
import { usePermissions } from '@/hooks/use-permissions';

const CATEGORY_OPTIONS: { value: DocumentCategory; label: string }[] = [
  { value: 'policy', label: 'Policy' },
  { value: 'procedure', label: 'Procedure' },
  { value: 'form', label: 'Form' },
  { value: 'template', label: 'Template' },
  { value: 'record', label: 'Record' },
  { value: 'manual', label: 'Manual' },
  { value: 'other', label: 'Other' },
];

function inferDocumentCategory(documentType: string | null): DocumentCategory {
  const words = (documentType ?? '').toLowerCase().split(/[^a-z]+/).filter(Boolean);
  const has = (word: string) => words.includes(word);
  if (has('certificate') || has('certificates')) return 'record';
  if (has('procedure') || has('procedures')) return 'procedure';
  if (has('policy') || has('policies')) return 'policy';
  if (has('form') || has('forms')) return 'form';
  if (has('template') || has('templates')) return 'template';
  if (has('manual') || has('manuals')) return 'manual';
  if (has('record') || has('records') || has('evidence')) return 'record';
  return 'other';
}

export function CreateDocumentPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const documentId = id ? Number(id) : 0;
  const isEditing = documentId > 0;
  const { user } = useAuth();
  const {
    data: ownerChoices,
    isLoading: usersLoading,
    isError: ownerChoicesFailed,
  } = useControlledDocumentOwnerOptions();
  const [searchParams] = useSearchParams();
  const clauseRef = searchParams.get('clause_ref');
  const documentType = searchParams.get('document_type');
  const createDocument = useCreateControlledDocument();
  const createAndApproveDocument = useCreateAndApproveControlledDocument();
  const updateDocument = useUpdateControlledDocument();
  const { data: existingDocument } = useControlledDocument(documentId);
  const { isAdmin } = usePermissions();

  const [title, setTitle] = useState('');
  const [documentNumber, setDocumentNumber] = useState('');
  const [category, setCategory] = useState<DocumentCategory>(() =>
    inferDocumentCategory(documentType),
  );
  const [hasEditedCategory, setHasEditedCategory] = useState(false);
  const [description, setDescription] = useState('');
  const [reviewIntervalDays, setReviewIntervalDays] = useState('365');
  const [retentionYears, setRetentionYears] = useState('7');
  const [requiresAcknowledgment, setRequiresAcknowledgment] = useState(false);
  const [relatedClauses, setRelatedClauses] = useState(clauseRef ? clauseRef : '');
  const [hasEditedRelatedClauses, setHasEditedRelatedClauses] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [initializedFromDocument, setInitializedFromDocument] = useState(false);
  const [owner, setOwner] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const ownerOptions = useMemo(() => {
    const options = (ownerChoices ?? []).map((option) => ({
      id: option.id,
      label: [option.first_name, option.last_name].filter(Boolean).join(' ') || option.username,
      isCurrentUser: option.id === user?.id,
    }));
    if (user && !options.some((option) => option.id === user.id)) {
      options.unshift({
        id: user.id,
        label: user.username || user.email,
        isCurrentUser: true,
      });
    }
    return options;
  }, [ownerChoices, user]);

  const shouldDefaultOwner =
    (ownerChoicesFailed && !ownerChoices) ||
    (ownerChoices !== undefined && ownerOptions.length === 1);
  const selectedOwner =
    owner || (shouldDefaultOwner && user ? String(user.id) : '');

  const clearFieldError = (field: string) => {
    setFieldErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const fieldError = (field: string) => fieldErrors[field]?.join(' ');

  useEffect(() => {
    if (!hasEditedCategory) {
      setCategory(inferDocumentCategory(documentType));
    }
  }, [documentType, hasEditedCategory]);

  useEffect(() => {
    if (!hasEditedRelatedClauses) {
      setRelatedClauses(clauseRef ?? '');
    }
  }, [clauseRef, hasEditedRelatedClauses]);

  useEffect(() => {
    if (!existingDocument || initializedFromDocument) return;
    setInitializedFromDocument(true);
    setTitle(existingDocument.title);
    setDocumentNumber(existingDocument.document_number);
    setCategory(existingDocument.category);
    setHasEditedCategory(true);
    setDescription(existingDocument.description);
    setReviewIntervalDays(String(existingDocument.review_interval_days));
    setRetentionYears(String(existingDocument.retention_years));
    setRequiresAcknowledgment(existingDocument.requires_acknowledgment);
    setRelatedClauses(existingDocument.related_clauses.join(', '));
    setHasEditedRelatedClauses(true);
    setOwner(String(existingDocument.owner));
  }, [existingDocument, initializedFromDocument]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});
    setFormError(null);

    if (!file && !isEditing) {
      setFieldErrors({ file: ['Please select a file to upload.'] });
      return;
    }
    if (!isEditing && !selectedOwner) {
      setFieldErrors({ owner: ['Select an owner.'] });
      return;
    }
    const submitter = (e.nativeEvent as SubmitEvent)
      .submitter as HTMLButtonElement | null;
    const shouldCreateAndApprove = submitter?.value === 'create-and-approve';

    const formData = new FormData();
    formData.append('title', title);
    if (!isEditing) {
      formData.append('owner', selectedOwner);
    }
    if (isEditing || documentNumber) {
      formData.append('document_number', documentNumber);
    }
    formData.append('category', category);
    if (isEditing || description) {
      formData.append('description', description);
    }
    formData.append('review_interval_days', reviewIntervalDays);
    formData.append('retention_years', retentionYears);
    formData.append('requires_acknowledgment', String(requiresAcknowledgment));
    if (relatedClauses.trim()) {
      const clauses = relatedClauses.split(',').map((c) => c.trim()).filter(Boolean);
      formData.append('related_clauses', JSON.stringify(clauses));
    } else if (isEditing) {
      formData.append('related_clauses', JSON.stringify([]));
    }
    if (file) formData.append('file', file);

    try {
      const doc = isEditing
        ? await updateDocument.mutateAsync({ id: documentId, formData })
        : shouldCreateAndApprove
          ? await createAndApproveDocument.mutateAsync(formData)
          : await createDocument.mutateAsync(formData);
      toast.success(`"${doc.title}" has been ${isEditing ? 'updated' : 'uploaded'}.`);
      navigate(`/compliance/documents/${doc.id}`);
    } catch (error) {
      const apiError = handleApiError(error);
      const errors = apiError.errors ?? {};
      setFieldErrors(errors);
      const nonFieldErrors = errors.non_field_errors ?? errors.detail;
      if (nonFieldErrors?.length) {
        setFormError(nonFieldErrors.join(' '));
      } else if (Object.keys(errors).length === 0) {
        setFormError(apiError.message);
      }
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/compliance/documents')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditing ? 'Edit Controlled Document' : 'Upload New Document'}
          </h1>
          <p className="text-gray-600 mt-1">
            {isEditing ? 'Update this draft document' : 'Add a controlled document to the register'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {formError && (
          <div className="mb-6 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
            {formError}
          </div>
        )}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Document Details */}
          <Card>
            <CardHeader>
              <CardTitle>Document Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    clearFieldError('title');
                  }}
                  placeholder="e.g., Safety Policy"
                  aria-invalid={Boolean(fieldError('title'))}
                  required
                />
                {fieldError('title') && (
                  <p className="mt-1 text-sm text-red-600">{fieldError('title')}</p>
                )}
              </div>
              <div>
                <Label htmlFor="document_number">Document Number</Label>
                <Input
                  id="document_number"
                  value={documentNumber}
                  onChange={(e) => {
                    setDocumentNumber(e.target.value);
                    clearFieldError('document_number');
                  }}
                  placeholder="e.g., POL-001"
                  aria-invalid={Boolean(fieldError('document_number'))}
                />
                {fieldError('document_number') && (
                  <p className="mt-1 text-sm text-red-600">{fieldError('document_number')}</p>
                )}
              </div>
              <div>
                <Label htmlFor="category">Category *</Label>
                <Select
                  value={category}
                  onValueChange={(v) => {
                    setHasEditedCategory(true);
                    setCategory(v as DocumentCategory);
                    clearFieldError('category');
                  }}
                >
                  <SelectTrigger id="category" aria-invalid={Boolean(fieldError('category'))}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORY_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fieldError('category') && (
                  <p className="mt-1 text-sm text-red-600">{fieldError('category')}</p>
                )}
              </div>
              <div>
                <Label htmlFor="owner">Owner *</Label>
                <Select
                  value={selectedOwner}
                  onValueChange={(value) => {
                    setOwner(value);
                    clearFieldError('owner');
                  }}
                  disabled={isEditing || usersLoading}
                >
                  <SelectTrigger id="owner" aria-invalid={Boolean(fieldError('owner'))}>
                    <SelectValue placeholder={usersLoading ? 'Loading owners...' : 'Select owner'} />
                  </SelectTrigger>
                  <SelectContent>
                    {ownerOptions.map((option) => (
                      <SelectItem key={option.id} value={String(option.id)}>
                        {option.label}{option.isCurrentUser ? ' (You)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fieldError('owner') && (
                  <p className="mt-1 text-sm text-red-600">{fieldError('owner')}</p>
                )}
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    clearFieldError('description');
                  }}
                  placeholder="Brief description of the document..."
                  rows={3}
                  aria-invalid={Boolean(fieldError('description'))}
                />
                {fieldError('description') && (
                  <p className="mt-1 text-sm text-red-600">{fieldError('description')}</p>
                )}
              </div>
              <div>
                <Label htmlFor="related_clauses">Related QP-300 Clauses</Label>
                <Input
                  id="related_clauses"
                  value={relatedClauses}
                  onChange={(e) => {
                    setHasEditedRelatedClauses(true);
                    setRelatedClauses(e.target.value);
                    clearFieldError('related_clauses');
                  }}
                  placeholder="e.g., 2.8, 3.1"
                  aria-invalid={Boolean(fieldError('related_clauses'))}
                />
                <p className="text-xs text-gray-500 mt-1">Comma-separated clause references</p>
                {fieldError('related_clauses') && (
                  <p className="mt-1 text-sm text-red-600">{fieldError('related_clauses')}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* File & Review Settings */}
          <Card>
            <CardHeader>
              <CardTitle>File & Review Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="file">
                  Document File {isEditing ? '(leave blank to keep current file)' : '*'}
                </Label>
                <div className="mt-1 border-2 border-dashed rounded-lg p-6 text-center">
                  <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <input
                    id="file"
                    type="file"
                    className="hidden"
                    onChange={(e) => {
                      setFile(e.target.files?.[0] || null);
                      clearFieldError('file');
                    }}
                    aria-invalid={Boolean(fieldError('file'))}
                  />
                  <label
                    htmlFor="file"
                    className="cursor-pointer text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    {file ? file.name : 'Click to select a file'}
                  </label>
                  {file && (
                    <p className="text-xs text-gray-500 mt-1">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  )}
                </div>
                {fieldError('file') && (
                  <p className="mt-1 text-sm text-red-600">{fieldError('file')}</p>
                )}
              </div>
              <div>
                <Label htmlFor="review_interval">Review Interval (days)</Label>
                <Input
                  id="review_interval"
                  type="number"
                  value={reviewIntervalDays}
                  onChange={(e) => {
                    setReviewIntervalDays(e.target.value);
                    clearFieldError('review_interval_days');
                  }}
                  min="1"
                  aria-invalid={Boolean(fieldError('review_interval_days'))}
                />
                <p className="text-xs text-gray-500 mt-1">Default: 365 days (annual review)</p>
                {fieldError('review_interval_days') && (
                  <p className="mt-1 text-sm text-red-600">{fieldError('review_interval_days')}</p>
                )}
              </div>
              <div>
                <Label htmlFor="retention">Retention Period (years)</Label>
                <Input
                  id="retention"
                  type="number"
                  value={retentionYears}
                  onChange={(e) => {
                    setRetentionYears(e.target.value);
                    clearFieldError('retention_years');
                  }}
                  min="1"
                  aria-invalid={Boolean(fieldError('retention_years'))}
                />
                {fieldError('retention_years') && (
                  <p className="mt-1 text-sm text-red-600">{fieldError('retention_years')}</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <input
                  id="requires_ack"
                  type="checkbox"
                  checked={requiresAcknowledgment}
                  onChange={(e) => setRequiresAcknowledgment(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="requires_ack" className="text-sm font-normal">
                  Requires employee acknowledgment
                </Label>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/compliance/documents')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            value="create"
            disabled={
              createDocument.isPending ||
              createAndApproveDocument.isPending ||
              updateDocument.isPending
            }
          >
            {(createDocument.isPending || updateDocument.isPending) && (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            )}
            {isEditing ? 'Save Changes' : 'Upload Document'}
          </Button>
          {!isEditing && isAdmin && (
            <Button
              type="submit"
              value="create-and-approve"
              disabled={createDocument.isPending || createAndApproveDocument.isPending}
            >
              {createAndApproveDocument.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Create & approve
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}
